import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, VenetianMask, ShieldCheck } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { useSpyScoreboard } from '../../components/games/spy/hooks'
import { cn } from '../../lib/utils'

export function SpyScoreboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scoreboard, loading } = useSpyScoreboard()

  const bestBluffer = scoreboard.find((r) => r.spyWins > 0) || null
  const mostCaught = [...scoreboard].sort((a, b) => b.caught - a.caught).find((r) => r.caught > 0) || null

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/games/spy')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('games.spy.scoreboard')}
        </h1>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && scoreboard.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
            {t('games.spy.noResults')}
          </CardContent>
        </Card>
      )}

      {!loading && scoreboard.length > 0 && (
        <>
          {(bestBluffer || mostCaught) && (
            <div className="grid grid-cols-2 gap-2">
              {bestBluffer && (
                <Card>
                  <CardContent className="p-3 text-center">
                    <VenetianMask className="w-5 h-5 mx-auto text-app-primary-500" />
                    <p className="text-[11px] text-muted-foreground mt-1" dir="auto">{t('games.spy.bestBluffer')}</p>
                    <p className="text-sm font-bold text-foreground truncate" dir="auto">{bestBluffer.name}</p>
                    <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.spyWinsN', { n: bestBluffer.spyWins })}</p>
                  </CardContent>
                </Card>
              )}
              {mostCaught && (
                <Card>
                  <CardContent className="p-3 text-center">
                    <ShieldCheck className="w-5 h-5 mx-auto text-emerald-500" />
                    <p className="text-[11px] text-muted-foreground mt-1" dir="auto">{t('games.spy.mostCaught')}</p>
                    <p className="text-sm font-bold text-foreground truncate" dir="auto">{mostCaught.name}</p>
                    <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.caughtN', { n: mostCaught.caught })}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-2 text-[11px] text-muted-foreground border-b border-border/60" dir="auto">
                <span>{t('games.spy.colPlayer')}</span>
                <span className="text-center">{t('games.spy.colGames')}</span>
                <span className="text-center">{t('games.spy.colSpyWins')}</span>
                <span className="text-center">{t('games.spy.colCaught')}</span>
              </div>
              {scoreboard.map((r, i) => (
                <div
                  key={r.name + i}
                  className={cn(
                    'grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-2.5 items-center',
                    i < scoreboard.length - 1 && 'border-b border-border/60',
                  )}
                  dir="auto"
                >
                  <span className="text-sm font-medium text-foreground truncate">{r.name}</span>
                  <span className="text-sm text-muted-foreground text-center tabular-nums">{r.games}</span>
                  <span className="text-sm text-app-primary-500 text-center tabular-nums font-semibold">{r.spyWins}</span>
                  <span className="text-sm text-emerald-500 text-center tabular-nums font-semibold">{r.caught}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

export default SpyScoreboardPage
